// SPDX-License-Identifier: Elastic-2.0
/**
 * ThemePill — segmented light/dark theme toggle.
 *
 * Two inline buttons (Light / Dark) inside a pill container. Visual
 * contract per `WizardShell.ThemePill`. Each button shows the matching
 * icon (`sun` / `moon`) followed by an i18n label.
 *
 * Two usage modes:
 *
 *  1. **Controlled** — pass `theme` + `onChange`. The component never
 *     touches the `useTheme` store; the parent owns state. Useful for
 *     previews, Storybook, or screens that want isolated state.
 *
 *  2. **Store-bound** — omit both props. The component reads/writes
 *     `useTheme` directly. `"auto"` mode is coerced for display to
 *     whichever of `light` / `dark` the system currently resolves to —
 *     but we *don't* mutate the underlying preference, so an "auto"
 *     user clicking the pill switches to the explicit mode they pick.
 *
 * Accessibility: the wrapper is `role="group"` with an i18n
 * `aria-label`. Each button is a real `<button>` with `aria-pressed`
 * for the toggle state, so the screen reader announces both the choice
 * and the current selection.
 */
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import { useTheme, type ThemeMode } from "../../theme/useTheme";

import styles from "./ThemePill.module.css";

export type ThemePillValue = "light" | "dark";

export interface ThemePillProps {
  /**
   * Controlled theme value. When provided alongside `onChange`, the
   * component is fully controlled and ignores the `useTheme` store.
   */
  theme?: ThemePillValue;
  /**
   * Change callback. Required if `theme` is provided. When omitted the
   * component falls back to calling `useTheme.setTheme` itself.
   */
  onChange?: (next: ThemePillValue) => void;
  /** Optional class for layout/positioning overrides. */
  className?: string;
}

/**
 * Coerce a `ThemeMode` (which includes `"auto"`) into the two-way
 * pill value. `"auto"` is treated as `"dark"` for display purposes —
 * the user's underlying preference stays `"auto"` until they click.
 */
function modeToPill(mode: ThemeMode): ThemePillValue {
  return mode === "light" ? "light" : "dark";
}

export function ThemePill({
  theme,
  onChange,
  className,
}: ThemePillProps): JSX.Element {
  const { t } = useTranslation();
  const storeTheme = useTheme((s) => s.theme);
  const setStoreTheme = useTheme((s) => s.setTheme);

  // Controlled if `theme` is supplied — otherwise read from the store.
  const current: ThemePillValue =
    theme !== undefined ? theme : modeToPill(storeTheme);

  const handleSelect = (next: ThemePillValue): void => {
    if (next === current) {
      return;
    }
    if (onChange) {
      onChange(next);
      return;
    }
    setStoreTheme(next);
  };

  const options: Array<{
    id: ThemePillValue;
    label: string;
    icon: "sun" | "moon";
  }> = [
    { id: "light", label: t("theme.light"), icon: "sun" },
    { id: "dark", label: t("theme.dark"), icon: "moon" },
  ];

  return (
    <div
      role="group"
      aria-label={t("theme.group_label")}
      className={[styles.root, className].filter(Boolean).join(" ")}
      data-testid="theme-pill"
    >
      {options.map((opt) => {
        const active = current === opt.id;
        const optionClass = active
          ? `${styles.option} ${styles.optionActive}`
          : styles.option;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            data-state={active ? "active" : "inactive"}
            data-testid={`theme-pill-${opt.id}`}
            className={optionClass}
            onClick={() => handleSelect(opt.id)}
          >
            <Icon name={opt.icon} size={14} stroke={1.75} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
